// React
import { memo, useCallback } from 'react'

// Components
import { BaseTableHeader, BaseTableFooter, BaseTableAction, BaseLabelStatus, BaseTableBody } from 'components'

// Antd
import { Button } from 'antd'
import { ColumnsType } from 'antd/es/table'

// i18n
import { useTranslation } from 'react-i18next'

// Interfaces
import { IUserList } from 'interfaces/back-office/user'
import { ITableEventAttrs } from 'interfaces/table'
import { IUserTableProps } from './interfaces'
import { USER_DETAIL_DIALOG_TYPE } from 'pages/back-office/user/components/UserWrapper/interfaces'

// Constant
import { USER_STATUS } from 'constant/shared.constant'
import { USER_URL } from 'constant/url.constant'

// React Router DOM
import { useHistory } from 'react-router-dom';

const UserTable = memo(({ status, data, loading, onChange, handleDialog }: IUserTableProps) => {
  // Hook
  const { t } = useTranslation()
  const history = useHistory();

  // Common State
  const columns: ColumnsType<IUserList> = [
    {
      key: 'number',
      title: t('app.table.number'),
      dataIndex: 'number',
    },
    {
      key: 'name',
      title: t('user.table.name'),
      dataIndex: 'name',
    },
    {
      key: 'companyName',
      title: t('user.table.company'),
      dataIndex: 'companyName',
    },
    {
      key: 'fieldName',
      title: t('app.user.companyData.form.field'),
      dataIndex: 'fieldName',
    },
    {
      key: 'accountStatus',
      title: t('user.table.accountStatus'),
      dataIndex: 'accountStatus',
      render: (_, record) => {
        return <BaseLabelStatus isBoolean value={record.accountStatus} />
      },
    },
    {
      key: 'x',
      title: t('app.table.action'),
      dataIndex: '',
      render: (_, record) => {
        const anotherActions = []

        // Check for user status
        if (status === USER_STATUS.WAITING_APPROVAL) {
          anotherActions.push(
            {
              label: t('user.action.approveRegistration'),
              onClick: () =>
                handleDialog('isApproveDialogOpen', true, {
                  editableDialogType: USER_DETAIL_DIALOG_TYPE.APPROVE_USER,
                  userId: record.id!,
                }),
            },
            {
              label: t('user.action.rejectRegistration'),
              onClick: () =>
                handleDialog('isRejectDialogOpen', true, {
                  editableDialogType: USER_DETAIL_DIALOG_TYPE.REJECT_USER,
                  userId: record.id!,
                }),
            },
          )
        } else if (status === USER_STATUS.APPROVED) {
          if (record.accountStatus) {
            anotherActions.push({
              label: t('user.action.deActivateUser'),
              onClick: () =>
                handleDialog('isConfirmationDialogOpen', true, {
                  editableDialogType: USER_DETAIL_DIALOG_TYPE.CHANGE_ACCOUNT_STATUS,
                  userId: record.id!
                }),
            })
          } else {
            anotherActions.push({
              label: t('user.action.activateUser'),
              onClick: () =>
                handleDialog('isConfirmationDialogOpen', true, {
                  editableDialogType: USER_DETAIL_DIALOG_TYPE.CHANGE_ACCOUNT_STATUS,
                  userId: record.id!
                }),
            })
          }
        } else {
          anotherActions.push({
            label: t('user.action.deleteData'),
            onClick: () => 
              handleDialog('isConfirmationDialogOpen', true, {
                userId: record.id!,
                editableDialogType: USER_DETAIL_DIALOG_TYPE.REMOVE_USER,
              })     
          })
        }

        return (
          <BaseTableAction
            isDelete={false}
            isEdit={false}
            onClickShow={() => history.push(`${USER_URL.USER_DETAIL}/${record.id}/profile`)}
            anotherAction={anotherActions}
          />
        )
      },
    },
  ]

  /**
   * @description Watch any change in table
   *
   * @param {ITableEventAttrs} event
   *
   * @return {void} void
   */
  const onChangeTable = useCallback(
    (event: ITableEventAttrs): void => {
      onChange(event)
    },
    [onChange],
  )

  /**
   * @description Render columns
   *
   * @return {ColumnsType<IUserList>} ColumnsType<IUserList>
   */
  const _columns = (): ColumnsType<IUserList> => {
    if (status === USER_STATUS.APPROVED) {
      return columns
      .map(column => ({
        ...column,
        width: `${columns.length / 100}%`,
      }))
    }
    return columns
      .map(column => ({
        ...column,
        width: `${columns.length / 100}%`,
      }))
      .filter(column =>  column.key !== 'accountStatus')
  }

  return (
    <>
      {/* Table Header */}
      <BaseTableHeader
        searchPlaceholder={t('user.formPlaceholder.searchUser')}
        onChange={onChangeTable}
        loading={loading.isLoading}
        right={
          <div className="flex flex-col md:flex-row justify-end">
            <Button
              type="primary"
              className="custom-button button-table-header mb-2 md:mb-0"
              loading={loading.isLoading}
              onClick={() => handleDialog('isBulkRegisterDialogOpen', true)}
            >
              {t('user.title.bulkInsert')}
            </Button>
          </div>
        }
      />

      {/* Table Body */}
      <BaseTableBody onChangeTable={onChangeTable} columns={_columns()} dataSource={[...data.list]} loading={loading.isLoading} />

      {/* Table Footer */}
      <BaseTableFooter onChange={onChangeTable} loading={loading.isLoading} data={data} />
    </>
  )
})

export { UserTable }
